import { useState } from "react";
import { AlertTriangle, Trash2, Loader2, Shield, ShieldCheck } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useAIConsent } from "../components/AIConsentDialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";
import { authAPI } from "../lib/api";

export default function SettingsPage({ user, onAccountDeleted }) {
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const { hasConsent, requestConsent } = useAIConsent();

  const handleWithdrawConsent = () => {
    localStorage.removeItem("archiva_ai_consent");
    window.location.reload();
  };

  const handleDelete = async () => {
    if (!password) {
      toast.error("Please enter your password");
      return;
    }
    if (confirmation.trim().toLowerCase() !== "delete") {
      toast.error("Please type delete to confirm");
      return;
    }
    setIsDeleting(true);
    try {
      // Normalize to uppercase so the backend receives a canonical value
      await authAPI.deleteAccount(password, "DELETE");
      toast.success("Your account has been permanently deleted");
      localStorage.removeItem("archiva_token");
      localStorage.removeItem("archiva_user");
      setDialogOpen(false);
      // Small delay so user sees the toast before logout
      setTimeout(() => {
        if (onAccountDeleted) onAccountDeleted();
        else window.location.href = "/";
      }, 1200);
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        "Could not delete account. Please try again.";
      toast.error(msg);
      setIsDeleting(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-6 md:py-10" data-testid="settings-page">
      <div className="mb-8">
        <h1 className="text-2xl sm:text-4xl font-bold tracking-tight mb-2">Settings</h1>
        <p className="text-muted-foreground">
          Manage your account and preferences.
        </p>
      </div>

      {/* Account info */}
      <div className="bg-white rounded-lg border border-border p-6 mb-6">
        <h2 className="text-lg font-semibold mb-4">Account</h2>
        <dl className="space-y-3">
          <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4">
            <dt className="text-sm font-medium text-muted-foreground sm:w-24">Name</dt>
            <dd className="text-sm">{user?.name || "—"}</dd>
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4">
            <dt className="text-sm font-medium text-muted-foreground sm:w-24">Email</dt>
            <dd className="text-sm break-all">{user?.email || "—"}</dd>
          </div>
        </dl>
      </div>

      {/* AI Data Processing */}
      <div className="bg-white rounded-lg border border-border p-6 mb-6">
        <div className="flex items-start gap-3 mb-4">
          {hasConsent ? (
            <ShieldCheck className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
          ) : (
            <Shield className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          )}
          <div>
            <h2 className="text-lg font-semibold">AI Data Processing</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Archiva sends your content to OpenAI for AI-powered features like tagging,
              search, chat, and writing assistance.
            </p>
          </div>
        </div>
        <div className="ml-8">
          {hasConsent ? (
            <div className="space-y-3">
              <p className="text-sm text-green-700">
                You have consented to AI data processing.
              </p>
              <Button variant="outline" size="sm" onClick={handleWithdrawConsent}>
                Withdraw Consent
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-amber-700">
                AI features are disabled. You have not consented to data processing.
              </p>
              <Button size="sm" onClick={requestConsent}>
                Review & Enable AI Features
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Danger zone — Delete account */}
      <div className="bg-white rounded-lg border-2 border-destructive/30 p-6">
        <div className="flex items-start gap-3 mb-4">
          <AlertTriangle className="w-5 h-5 text-destructive flex-shrink-0 mt-0.5" />
          <div>
            <h2 className="text-lg font-semibold text-destructive">Delete Account</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Permanently delete your Archiva account and all your data. This
              action cannot be undone.
            </p>
          </div>
        </div>

        <ul className="text-sm text-muted-foreground space-y-1 mb-4 ml-8 list-disc">
          <li>All your uploaded files will be deleted</li>
          <li>All your projects and AI chats will be deleted</li>
          <li>All your stories and chapters will be deleted</li>
          <li>You will be removed from stories shared with you</li>
          <li>Your email will no longer be registered</li>
        </ul>

        <AlertDialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <AlertDialogTrigger asChild>
            <Button
              variant="destructive"
              className="ml-8"
              data-testid="delete-account-button"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete My Account
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent className="max-w-md !bg-white text-slate-900 border border-slate-200 shadow-2xl">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-red-600 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5" />
                Delete account permanently?
              </AlertDialogTitle>
              <AlertDialogDescription className="text-slate-600">
                This will permanently delete your account and ALL your data.
                This cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>

            <div className="space-y-4 py-2">
              <div className="space-y-2">
                <Label htmlFor="delete-password" className="text-slate-900">
                  Enter your password
                </Label>
                <Input
                  id="delete-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Your password"
                  autoComplete="current-password"
                  className="bg-white text-slate-900 border-slate-300"
                  data-testid="delete-password-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="delete-confirmation" className="text-slate-900">
                  Type <span className="font-mono font-bold">delete</span> to confirm
                </Label>
                <Input
                  id="delete-confirmation"
                  value={confirmation}
                  onChange={(e) => setConfirmation(e.target.value)}
                  placeholder="delete"
                  className="bg-white text-slate-900 border-slate-300"
                  data-testid="delete-confirmation-input"
                />
              </div>
            </div>

            <AlertDialogFooter>
              <AlertDialogCancel
                onClick={() => {
                  setPassword("");
                  setConfirmation("");
                }}
                disabled={isDeleting}
                className="bg-white text-slate-900 border-slate-300 hover:bg-slate-50"
              >
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={(e) => {
                  e.preventDefault();
                  handleDelete();
                }}
                disabled={isDeleting || confirmation.trim().toLowerCase() !== "delete" || !password}
                className="!bg-red-600 hover:!bg-red-700 !text-white disabled:!bg-red-300 disabled:cursor-not-allowed"
                data-testid="confirm-delete-account"
              >
                {isDeleting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  "Delete Forever"
                )}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
}
